import {OnboardingStepType} from '@toolify/server/src/enum/OnboardingStepType'
import {
  IUserResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {
  IWorkspaceResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWorkspaceResponseInstance'
import {
  isUserLevelOnboardingStep,
  onboardingStepOrder,
} from '@toolify/client/src/modules/onboarding/enums/onboardingStepMetadataMap'

export interface IResolvedOnboardingState {
  pendingSteps: OnboardingStepType[]
  isComplete: boolean
  isKnown: boolean
}

export interface IResolveOnboardingStateOptions {
  canEditWorkspace?: boolean | null
}

export function hasWorkspaceOnboardingData(workspace?: IWorkspaceResponseInstance | null): boolean {
  return !!workspace && workspace.skippedOnboardingSteps !== undefined
}

export function hasUserOnboardingData(user?: IUserResponseInstance | null): boolean {
  return !!user && user.skippedOnboardingSteps !== undefined
}

export function isOnboardingStepSatisfied(
  step: OnboardingStepType,
  workspace: IWorkspaceResponseInstance,
  user: IUserResponseInstance,
): boolean {
  const workspaceSkipped = workspace.skippedOnboardingSteps?.includes(step) ?? false
  const userSkipped = user.skippedOnboardingSteps?.includes(step) ?? false

  switch(step) {
  case OnboardingStepType.WorkspaceCustomization:
    // Every workspace already has a title and a logo upload must not advance the
    // step under the user, so only Continue/Skip (both record a skip) complete it
    return workspaceSkipped
  case OnboardingStepType.CompanyInfo:
    return workspaceSkipped || !!(workspace.employeeRange && workspace.teamRange)
  case OnboardingStepType.CoreAreas:
    return workspaceSkipped || (workspace.coreAreas?.length ?? 0) > 0
  case OnboardingStepType.Solutions:
    return workspaceSkipped || (workspace.solutions?.length ?? 0) > 0
  case OnboardingStepType.Invite:
    return workspaceSkipped
  case OnboardingStepType.RoleAndDiscover:
    return userSkipped || !!(user.role && user.discoverSource)
  case OnboardingStepType.GenerateWorkspace:
    return workspaceSkipped || !!workspace.pendingOnboardingPlan
  case OnboardingStepType.ProfileCustomization:
    return userSkipped || !!user.nick
  default:
    return false
  }
}

export function resolveOnboardingState(
  workspace?: IWorkspaceResponseInstance | null,
  user?: IUserResponseInstance | null,
  options: IResolveOnboardingStateOptions = {},
): IResolvedOnboardingState {
  if(!workspace || !user) {
    return {
      pendingSteps: [],
      isComplete: true,
      isKnown: false,
    }
  }
  const hasWorkspaceData = hasWorkspaceOnboardingData(workspace)
  const hasUserData = hasUserOnboardingData(user)
  const canEditWorkspace = options.canEditWorkspace !== false
  const pendingSteps = onboardingStepOrder.filter(step => {
    const isEvaluable = isUserLevelOnboardingStep(step) ? hasUserData : (hasWorkspaceData && canEditWorkspace)
    return isEvaluable && !isOnboardingStepSatisfied(step, workspace, user)
  })
  return {
    pendingSteps,
    isComplete: pendingSteps.length === 0,
    isKnown: hasWorkspaceData && hasUserData,
  }
}
